import axios from './axiosInstance';
import { APICustomRoleDefinition, APIUserRoleIAMStatement } from '../../types';

// Custom, area-scoped role definitions (UllrServerless
// ullrApi/src/roleDefinitions). Authoring needs `roleDefinitions:*`, which only
// the resort's super admin holds. The merged catalog used for assignment is
// still `roles.getRoleDefinitions`; these calls are for the management UI.

interface SportParkParams {
  sport: string;
  parkId: string;
}

interface CreateCustomRoleDefinitionParams extends SportParkParams {
  slug: string;
  name: string;
  /** Sent only when set (the onboarding-pack install passes its role's description). */
  description?: string;
  statements: APIUserRoleIAMStatement[];
  /** `x-request-id`, for a create that must not happen twice. */
  requestId?: string;
}

interface UpdateCustomRoleDefinitionParams extends SportParkParams {
  slug: string;
  name?: string;
  description?: string;
  statements?: APIUserRoleIAMStatement[];
}

interface DeleteCustomRoleDefinitionParams extends SportParkParams {
  slug: string;
}

const listCustomRoleDefinitions = ({ sport, parkId }: SportParkParams): Promise<APICustomRoleDefinition[]> => {
  return axios.get(`/${sport}/areas/${parkId}/roles/customdefinitions`)
    .then((response) => response.data)
    .catch((error) => {
      console.log(error);
      throw error;
    });
};

const createCustomRoleDefinition = ({
  sport, parkId, slug, name, description, statements, requestId,
}: CreateCustomRoleDefinitionParams): Promise<APICustomRoleDefinition> => {
  const url = `/${sport}/areas/${parkId}/roles/customdefinitions`;
  const body = { slug, name, ...(description !== undefined ? { description } : {}), statements };
  const request = requestId
    ? axios.put(url, body, { headers: { 'x-request-id': requestId } })
    : axios.put(url, body);
  return request
    .then((response) => response.data)
    .catch((error) => {
      console.log(error);
      throw error;
    });
};

const updateCustomRoleDefinition = ({
  sport, parkId, slug, name, description, statements,
}: UpdateCustomRoleDefinitionParams): Promise<APICustomRoleDefinition> => {
  const body: Record<string, unknown> = {};
  if (name !== undefined) {
    body.name = name;
  }
  if (description !== undefined) {
    body.description = description;
  }
  if (statements !== undefined) {
    body.statements = statements;
  }
  return axios.post(`/${sport}/areas/${parkId}/roles/customdefinitions/${slug}`, body)
    .then((response) => response.data)
    .catch((error) => {
      console.log(error);
      throw error;
    });
};

const deleteCustomRoleDefinition = ({ sport, parkId, slug }: DeleteCustomRoleDefinitionParams): Promise<any> => {
  return axios.delete(`/${sport}/areas/${parkId}/roles/customdefinitions/${slug}`)
    .then((response) => response.data)
    .catch((error) => {
      console.log(error);
      throw error;
    });
};

const roleDefinitionFunctions = {
  listCustomRoleDefinitions,
  createCustomRoleDefinition,
  updateCustomRoleDefinition,
  deleteCustomRoleDefinition,
};

export default roleDefinitionFunctions;
